import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TemplateLayoutService } from '@fi-app/template/template-layout.service';
import { TemplateLayoutLanguageService } from '@fi-app/template/template-layout-language.service';
import { cloneDeep } from '@fi-shared/utils/clone-deep';
import { FiDocumentTemplateOptionsService } from '@fi-app/document-template/document-template-options.service';
import { FiDocumentTemplateLayoutTrashBinService } from '@fi-app/document-template/document-template-layout-trash-bin.service';
import { AppTextService } from '@fi-shared/app-text';
import {
    DialogYesNoInputComponent,
    IDialogYesNoInputData,
} from '@fi-app/utilities/dialog/dialog-yes-no-input/dialog-yes-no-input.component';
import { DocumentTemplateService } from '@fi-app/document-template/document-template.service';
import { LoadingService } from '@fi-app/shared/fi-loading/loading.service';
import { TemplateModel } from '@fi-app/template/template.model';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { TemplateControlType } from '@fi-app/template/types/template-control-type';
import { isDefined } from '@fi-shared/utils';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { isNullOrUndefined } from 'util';
import { FiInsertMedia2Service } from '@fi-shared/fi-insert-media/fi-insert-media2.service';
import { ObjectTypes } from '@fi-shared/types';
import { IInsertMediaResult } from '@fi-shared/fi-insert-media/fi-insert-media';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TemplateLayoutModel } from '@fi-app/template/template-layout.model';
import { removeItem } from '@fi-shared/utils/remove-item';
import { BaseItemModelService, IDocumentTemplateBaseItem } from '@fi-app/template/base-item.model';
import { ImageMovieFileTypes } from '@fi-shared/fi-insert-media/fi-insert-media-consts';
import { getAcceptByFileType } from '@fi-shared/utils/get-accept-by-file-type';

@Component({
    selector: 'fi-document-template-layout-edit',
    templateUrl: './document-template-layout-edit.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['../document-template-layout.scss'],
    standalone: false,
})
export class FiDocumentTemplateLayoutEditComponent implements OnInit, OnDestroy {
    @Input() tplLayout: TemplateLayoutModel;
    @Input() template: TemplateModel;

    constructor(
        private templateLayoutService: TemplateLayoutService,
        private templateLayoutLanguageService: TemplateLayoutLanguageService,
        private documentTemplateOptionsService: FiDocumentTemplateOptionsService,
        private documentTemplateLayoutTrashBinService: FiDocumentTemplateLayoutTrashBinService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private documentTemplateService: DocumentTemplateService,
        private loadingService: LoadingService,
        private fiBrowserEventsService: FiBrowserEventsService,
        private fiInsertMedia2Service: FiInsertMedia2Service,
    ) {}

    ngOnInit() {
        this.documentTemplateOptionsService.onRemoveFileFromSelectedItem$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((imageElement: HTMLElement) => {
                const currentContent = this.getCurrentContent();
                const columnNumber = this.getCurrentColumnNumber(imageElement);
                if (isDefined(currentContent) && !!columnNumber) {
                    this.tplLayout.templateLayoutHelper.deleteTemplatePlaceHolderImageContent(
                        currentContent,
                        columnNumber,
                    );
                }
            });
    }

    isSelected(item) {
        return this.documentTemplateOptionsService.selectedItem == item;
    }

    TemplateItemSelected($event: MouseEvent, item: IDocumentTemplateBaseItem) {
        this.documentTemplateOptionsService.selectedItem = item;
        this.openAddMediaBankDialog($event);
    }

    TemplateItemMoved(items: BaseItemModelService[], item: BaseItemModelService, event: DragEvent) {
        if (this.documentTemplateLayoutTrashBinService.dropItem === true) {
            this._templateItemMovedTrashConfirm().onYesButtonClick(() => {
                this._templateItemMovedTrashConfirmInUse().then(() => {
                    this.documentTemplateOptionsService.selectedItem = undefined;
                    removeItem(items, item);
                });
            });
        } else {
            removeItem(items, item);
        }
    }

    _templateItemMovedTrashConfirm() {
        return this.fiDialogsService.confirm({
            messageText: this.appTextService.get('dialog.delete_confirm'),
            hideHeader: true,
        });
    }

    _isInUsePromise: Promise<number>;

    _isInUse() {
        if (!this._isInUsePromise) {
            this._isInUsePromise = new Promise<number>((resolve, reject) => {
                if (!this.template.Id) {
                    resolve(0);
                } else {
                    this.loadingService
                        .trackPromise(this.documentTemplateService.IsTemplateInUsed(this.template.Id))
                        .then((inUseResult) => resolve(inUseResult), reject);
                }
            });
        }

        return this._isInUsePromise;
    }

    _templateItemMovedTrashConfirmInUse() {
        return new Promise<void>((resolve, reject) => {
            this._isInUse().then((inUseResult) => {
                if (inUseResult > 0) {
                    const compareValue = this.appTextService.get('common.delete').toUpperCase();
                    const dialogConfirmData: IDialogYesNoInputData = {
                        message: this.appTextService.get('document.delete_content_block_warning', {
                            '{0}': compareValue,
                            '{1}': inUseResult,
                        }),
                        buttonText: this.appTextService.get('common.confirm'),
                        confirmTextToInput: compareValue,
                    };
                    this.fiDialogsService
                        .custom({
                            headerText: this.appTextService.get('dialog.header_confirm'),
                            contentComponentRef: DialogYesNoInputComponent,
                            contentComponentData: dialogConfirmData,
                            config: {
                                maxWidth: '400px',
                            },
                        })
                        .onYesButtonClick(() => resolve());
                } else {
                    resolve();
                }
            }, reject);
        });
    }

    TemplateItemDragStart(list: BaseItemModelService[], item: BaseItemModelService, $event: DragEvent) {
        this.documentTemplateOptionsService.selectedItem = undefined;
        this.documentTemplateLayoutTrashBinService.dropItem = false;
        this.documentTemplateLayoutTrashBinService.isShow = true;
    }

    TemplateItemDragEnd() {
        this.documentTemplateLayoutTrashBinService.isShow = false;
    }

    TemplateItemDrop($event, parent) {
        const itemPrototype = this.templateLayoutService.GetLayoutItemPrototypeByControlType($event.data.controlId);
        const newItem = Object.assign(cloneDeep(itemPrototype), $event.data);
        this.documentTemplateOptionsService.selectedItem = newItem;
        if (!parent.items) {
            parent.items = [];
        }
        parent.items.splice($event.index, 0, newItem);
    }

    isEmptyItem(item) {
        return item.IsEmpty(this.templateLayoutLanguageService.selectedLanguage);
    }

    private openAddMediaBankDialog($event: MouseEvent): void {
        const column: number = this.getCurrentColumnNumber($event.target as HTMLElement);
        if (
            !column ||
            this.isAlreadyAddedImage(column) ||
            this.documentTemplateOptionsService.selectedItem.controlId !== TemplateControlType.Image
        ) {
            return;
        }
        this.fiInsertMedia2Service
            .openInsertMediaDialogWithFileSizes(ObjectTypes.Template, {
                accept: getAcceptByFileType(ImageMovieFileTypes, undefined),
                fileTypes: ImageMovieFileTypes,
            })
            .onYesButtonClick((result: IInsertMediaResult) => {
                const currentContent = this.getCurrentContent();
                this.tplLayout.templateLayoutHelper.setTemplatePlaceHolderImageContent(
                    currentContent,
                    column,
                    result.mediaBankFileReferences[0],
                );
            });
    }

    private getCurrentColumnNumber(htmlElement: HTMLElement): number {
        const documentLayoutItemImageColumnElement: HTMLElement = htmlElement.closest(
            '.fi-document-layout-item-image-column',
        );
        if (isNullOrUndefined(documentLayoutItemImageColumnElement)) {
            return;
        }
        return +(documentLayoutItemImageColumnElement as Node).parentElement.getAttribute('data-column');
    }

    private isAlreadyAddedImage(column: number): boolean {
        const currentContent = this.getCurrentContent();
        return column === 1
            ? currentContent.Value.hasOwnProperty('File') && currentContent.Value.File.hasOwnProperty('FileType')
            : currentContent.Value.hasOwnProperty('additionalFile') &&
                  currentContent.Value.additionalFile.hasOwnProperty('FileType');
    }

    private getCurrentContent() {
        return this.documentTemplateOptionsService.selectedItem.model.Contents.find((content) => {
            return content.ContentLanguageId === this.templateLayoutLanguageService.selectedLanguage;
        });
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
        this.documentTemplateOptionsService.selectedItem = undefined;
    }
}
